import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { SelectOption } from '../components/aiImageSuggestions/AiImageSuggestionsForm';

export const TYPE_OPTIONS = [
  { value: 'product', label: 'Product Showcase' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'behind_scenes', label: 'Behind-the-Scenes' },
  { value: 'testimonial', label: 'Testimonial' },
  { value: 'poll', label: 'Poll/Question' },
  { value: 'quote', label: 'Quote Card' },
  { value: 'meme', label: 'Meme' },
  { value: 'story', label: 'Story Highlight' },
  { value: 'infographic', label: 'Infographic' },
  { value: 'tutorial', label: 'Tutorial/How-to' },
  { value: 'data_viz', label: 'Data Visualization' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'before_after', label: 'Before/After' },
  { value: 'event', label: 'Event Promotion' },
  { value: 'carousel', label: 'Carousel Preview' },
];

export const STYLE_OPTIONS: OptionsOrGroups<
  SelectOption<string>,
  GroupBase<SelectOption<string>>
> = [
  {
    label: 'Photography',
    options: [
      { value: 'realistic', label: 'Realistic' },
      { value: 'analog-film', label: 'Analog Film' },
    ],
  },
  {
    label: 'Artistic',
    options: [
      { value: 'artistic', label: 'Artistic' },
      { value: 'anime', label: 'Anime' },
      { value: 'digital-art', label: 'Digital Art' },
      { value: 'fantasy-art', label: 'Fantasy Art' },
      { value: 'vaporwave', label: 'Vaporwave' },
      { value: 'isometric', label: 'Isometric' },
      { value: 'low-poly', label: 'Low Poly' },
      { value: 'claymation', label: 'Claymation' },
      { value: 'origami', label: 'Origami' },
      { value: 'line-art', label: 'Line Art' },
      { value: 'pixel-art', label: 'Pixel Art' },
    ],
  },
];

export const IMAGE_COUNT_OPTIONS = [
  { value: 1, label: '1 image' },
  { value: 2, label: '2 images' },
  { value: 4, label: '4 images' },
];

const COMMON_ORIENTATIONS = [
  { value: '1:1', label: 'Square (1:1)' },
  { value: '4:5', label: 'Vertical (4:5)' },
  { value: '16:9', label: 'Landscape (16:9)' },
];

const PLATFORM_SPECIFIC_ORIENTATIONS = {
  [TYPE.INSTAGRAM]: [
    ...COMMON_ORIENTATIONS,
    { value: '9:16', label: 'Stories/Reels (9:16)' },
  ],
  [TYPE.TWITTER]: [
    { value: '16:9', label: 'Landscape (16:9)' },
    { value: '1:1', label: 'Square (1:1)' },
    { value: '2:1', label: 'Wide format (2:1)' },
  ],
  [TYPE.PINTEREST]: [
    { value: '2:3', label: 'Portrait (2:3)' },
    { value: '1:1', label: 'Square (1:1)' },
    { value: '9:16', label: 'Story Pins (9:16)' },
  ],
  [TYPE.TIKTOK]: [
    { value: '9:16', label: 'Vertical (9:16)' },
    ...COMMON_ORIENTATIONS,
  ],
};

export const ORIENTATION_OPTIONS = {
  [TYPE.FACEBOOK]: COMMON_ORIENTATIONS,
  [TYPE.LINKEDIN]: [
    { value: '4:5', label: 'Vertical (4:5)' },
    { value: '1:1', label: 'Square (1:1)' },
    { value: '16:9', label: 'Landscape (16:9)' },
  ],
  [TYPE.THREADS]: COMMON_ORIENTATIONS,
  [TYPE.GOOGLE_MY_BUSINESS]: COMMON_ORIENTATIONS,
  ...PLATFORM_SPECIFIC_ORIENTATIONS,
} as {
  [key: number]: { value: string; label: string }[];
};

export const AI_IMAGE_GENERATION_TIPS = {
  title: 'Master AI Image Generation for Social Media',
  categories: [
    {
      category: 'Reference & Remix',
      colorClass: 'tw-bg-purple-100',
      tips: [
        'Use references: Upload 1–5 images to steer subject, composition, or style.',
        'Remix results: Iterate on a generated image to refine subject, lighting, or style.',
        'Preserve essence: When remixing, keep core elements while applying prompt changes.',
        'Blend sources: Combine multiple references to merge styles or subjects coherently.',
      ],
    },
    {
      category: 'Best Practices (Social Media)',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Clear subject: Focus on a single, strong focal point and avoid clutter.',
        'Scroll-stopping contrast: Use bold color contrast and clean composition to catch attention in feeds.',
        'Concise text: Modern models can render on-image text—keep it short, high-contrast, and highly legible.',
        'Safe zones: Keep important elements away from edges to prevent platform cropping.',
      ],
    },
    {
      category: 'Format Tips',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Memes: Bold background, expressive faces, and space for top/bottom text if needed.',
        'Infographics: Use simple charts/icons, 2–3 brand colors, and clear visual hierarchy.',
        'Quotes: Prioritize typography legibility with subtle, non-distracting backgrounds.',
        'Product: Clean background, flattering light, and angles that highlight key features.',
      ],
    },
    {
      category: 'Aspect Ratio Guidance',
      colorClass: 'tw-bg-primary-100',
      tips: [
        '1:1 Square: Centered subject and symmetric compositions work best for grids.',
        '4:5 Portrait: Mobile-first; keep headings in the upper third for impact.',
        '16:9 Landscape: Use for wide scenes and banners; keep message in safe center.',
        '9:16 Stories/Reels: Reserve top/bottom for UI; design for vertical motion.',
        '2:3 Pinterest-like: Tall visuals and step-by-step or vertical narratives.',
        '1.91:1 Link previews: Banner-style layouts with strong left-to-right flow.',
      ],
    },
    {
      category: 'Style Selection',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Match audience: Pick a style that fits your brand voice and platform culture.',
        'Consistency: Use one style across a carousel or campaign for cohesion.',
        'Realistic vs. stylized: Use realistic for products/people; stylized for concepts.',
        'Preview first: Choose styles by their thumbnails and how they read at a glance.',
      ],
    },
    {
      category: 'Variants Strategy',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Generate variants: Use more variants to explore composition and mood options.',
        'Select and iterate: Pick the strongest option and remix to improve details.',
        'Prompt micro-changes: Adjust adjectives, lighting, or angle to broaden exploration.',
      ],
    },
    {
      category: 'Model Tips (modern generators)',
      colorClass: 'tw-bg-purple-100',
      tips: [
        'Specific prompts: Include subject, style adjectives, camera angle, lighting, and mood.',
        'Text in image: Modern models can render text reliably—specify exact wording, casing, placement (e.g., "top center"), size, and style (font, weight).',
        'Audience intent: Mention goal and audience to guide composition choices.',
        'Content policy: Keep prompts safe and suitable for all audiences.',
      ],
    },
  ],
} as const;

export const AI_IMAGE_EDIT_TIPS = {
  title: 'Tips for Editing Translated Images',
  categories: [
    {
      category: 'How It Works',
      colorClass: 'tw-bg-purple-100',
      tips: [
        'Preservation mode: The AI keeps your image intact and only applies the changes you describe.',
        'Reference image: Your translated image is automatically loaded as a reference — no need to upload.',
        'Iterate: Generate, review, and remix until the result matches your expectations.',
      ],
    },
    {
      category: 'Effective Prompts',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Be specific: "Change the headline to Happy Birthday" works better than "fix the text".',
        'One change at a time: Focus on a single edit per generation for best results.',
        'Mention position: "Change the text in the top-left corner" helps the AI target the right area.',
        'Font guidance: "Keep the same bold font style" reminds the AI to preserve typography.',
      ],
    },
    {
      category: 'Common Use Cases',
      colorClass: 'tw-bg-primary-100',
      tips: [
        'Fix translation text: Correct words that were translated incorrectly or are too long.',
        'Adjust spacing: Ask to "fit the translated text within the original text area".',
        'Fix artifacts: "Remove the blurry area around the text" to clean up AI artifacts.',
        'Style consistency: "Match the font and color of the original design" for brand compliance.',
      ],
    },
  ],
} as const;
